import { createRef, useCallback, useEffect, useState } from 'react';
import CustomForm from 'components/form/CustomForm';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import uuid from 'react-uuid';
import Form from '@rjsf/core';
import { SectionTitle } from 'components/form/SectionTitle';
import { AddFieldButton, RemoveFieldButton, SchemaPropertyCard } from './Form.styles';
import {
  LLMSummarizeConfiguration,
  LLMSummarizeSchemaProperty,
  LLMSummarizeSchema,
  CreateWorkflowNode,
} from 'api/model';
import { NodeFormProps, schemaLLMSummarizeSchemaProperty } from 'api/workflows-steps/formSchema';
import { toast } from 'react-toastify';
import { useNodeValidation } from 'hooks/useNodeValidation';
import LLMCommonForm from './LLMCommonForm';

type SchemaSummarizeProperty = {
  id: string;
  ref?: React.MutableRefObject<Form>;
  data?: LLMSummarizeSchemaProperty;
};

const SummarizeSchemaForm = (props: NodeFormProps) => {
  const { initialValues = {} as CreateWorkflowNode } = props;
  const [formData, setFormData] = useState<CreateWorkflowNode>(initialValues);
  const [summarizeSchemaProperty, setSummarizeSchemaProperty] = useState<SchemaSummarizeProperty[]>([]);
  const { validate } = useNodeValidation(initialValues.type);

  useEffect(() => {
    const init = initialValues;
    if (init.configuration) {
      const conf = init.configuration as LLMSummarizeConfiguration;
      const buff: SchemaSummarizeProperty[] = [];
      for (const p in conf.summarize_schema.properties) {
        const data = conf.summarize_schema.properties[p];
        buff.push({
          id: uuid(),
          ref: createRef() as React.MutableRefObject<Form>,
          data: {
            ...data,
            column_name: p,
            word_count: data.word_count,
          } as LLMSummarizeSchemaProperty,
        });
      }
      setSummarizeSchemaProperty(buff);
    }
  }, [initialValues]);

  const handleSubmit = useCallback(async () => {
    const isValid: boolean = summarizeSchemaProperty.filter((p) => !p.ref?.current?.validateForm()).length == 0;

    const conf = formData.configuration as LLMSummarizeConfiguration | undefined;
    if (
      !conf?.summarize_schema ||
      !conf.summarize_schema.properties ||
      Object.keys(conf.summarize_schema.properties).length === 0
    ) {
      toast.error("Summarize schema can't be empty! Add some fields.");
      return;
    }
    if (isValid && (await validate(formData))) {
      props.onSubmit?.(formData);
    }
  }, [formData, props.onSubmit, summarizeSchemaProperty]);

  const handleBaseFormOnChange = (formDataToChange: CreateWorkflowNode) => {
    const newFormData = {
      ...formDataToChange,
      configuration: {
        ...formDataToChange.configuration,
        summarize_schema: (formData.configuration as LLMSummarizeConfiguration | undefined)?.summarize_schema,
      },
    };
    setFormData(newFormData);
    props.onChange?.(newFormData);
  };

  const handlePropertyOnChange = (tempFormData: LLMSummarizeSchemaProperty, id: string) => {
    const property = summarizeSchemaProperty.find((p) => p.id == id);
    if (property) {
      const index = summarizeSchemaProperty.indexOf(property);
      if (index != -1) {
        summarizeSchemaProperty[index] = {
          ...property,
          data: tempFormData,
        } as SchemaSummarizeProperty;
      }
    }
    setSummarizeSchemaProperty(summarizeSchemaProperty);

    updateFormData(summarizeSchemaProperty);
  };

  const updateFormData = (summarizeProperties: SchemaSummarizeProperty[]) => {
    const summarizeSchema: LLMSummarizeSchema = {
      properties: summarizeProperties.reduce(
        (a, p) => ({
          ...a,
          [p.data?.column_name ?? '']: {
            type: p.data?.type,
            description: p.data?.description,
            word_count: p.data?.word_count,
          },
        }),
        {},
      ),
      required: [],
    };

    setFormData({ ...formData, configuration: { ...formData.configuration, summarize_schema: summarizeSchema } });
  };

  const handleAddSummarizeSchemaProperty = () => {
    setSummarizeSchemaProperty([
      ...summarizeSchemaProperty,
      {
        id: uuid(),
        ref: createRef(),
      } as SchemaSummarizeProperty,
    ]);
  };

  const handleDeleteSummarizeSchemaProperty = (id: string) => {
    const properties = summarizeSchemaProperty.filter((p) => p.id != id);
    setSummarizeSchemaProperty(properties);
    updateFormData(properties);
  };

  return (
    <>
      <LLMCommonForm {...props} onSubmit={handleSubmit} onChange={handleBaseFormOnChange} validate={false} />
      <SectionTitle title="Summarize schema" />

      <div className="flex flex-col">
        {summarizeSchemaProperty.map((p) => (
          <SchemaPropertyCard key={p.id}>
            <CustomForm<LLMSummarizeSchemaProperty>
              schema={schemaLLMSummarizeSchemaProperty}
              onSubmit={handleSubmit}
              onChange={(data) => handlePropertyOnChange(data, p.id)}
              initialValues={p.data}
              formId={`${props.formId}-${p.id}`}
              ref={p.ref}
              className="w-full"
              disabled={props.disabled}
            />
            <div className="flex items-center pl-1">
              <RemoveFieldButton
                aria-label="delete"
                size="small"
                onClick={() => handleDeleteSummarizeSchemaProperty(p.id)}
                disabled={props.disabled}
              >
                <DeleteIcon fontSize="small" />
              </RemoveFieldButton>
            </div>
          </SchemaPropertyCard>
        ))}
        <AddFieldButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddSummarizeSchemaProperty}
          disabled={props.disabled}
        >
          Add summarize field
        </AddFieldButton>
      </div>
    </>
  );
};

export default SummarizeSchemaForm;
